import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { SearchBar, TableFlat, FormModal, Button } from "@veorest/ui";
import { Building2, Wallet, Plus, Copy, Check, Handshake, AlertTriangle, Eye, Pencil, Power, Trash2 } from "lucide-react";
import {
  createAdminBusinessOnboarding,
  deleteAdminClient,
  fetchAdminClient,
  fetchAdminClients,
  fetchAdminPartners,
  updateAdminClientProfile,
  updateAdminClientStatus,
  type AdminClient,
} from "@veorest/api-client";
import { clientDetailPath } from "../routes";
import { PageHeader } from "../components/shell/PageHeader";
import { AdminPager, AdminPanel } from "../components/shell/AdminPanel";
import { EmptyState } from "../components/data/EmptyState";
import { formatDate, formatIqd, formatNumber, statusPillClass } from "../lib/format";
import { clientTypeLabel, clientTypeStatusLabel } from "../lib/platformModel";

function friendlyClientError(err: unknown): string {
  const m = (err instanceof Error ? err.message : String(err ?? "")).toLowerCase();
  if (m.includes("username_taken")) return "Username already taken — choose another.";
  if (m.includes("username_reserved")) return "That username is reserved.";
  if (m.includes("username_invalid") || m.includes("username_required")) {
    return "Invalid username: 3–30 chars (a-z, 0-9, dash only).";
  }
  if (m.includes("client_not_found")) return "Client not found.";
  return err instanceof Error ? err.message : "Request failed";
}

const STATUS_FILTERS = [
  { key: "", label: "All" },
  { key: "active", label: "Active" },
  { key: "trial", label: "Trial" },
  { key: "onboarding", label: "Onboarding" },
  { key: "suspended", label: "Suspended" },
  { key: "cancelled", label: "Cancelled" },
] as const;

const KIND_FILTERS = [
  { key: "", label: "All types" },
  { key: "oelox", label: "OELOX clients" },
  { key: "partner", label: "Partner clients" },
  { key: "needs_review", label: "Needs review" },
] as const;

export default function ClientsPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [kind, setKind] = useState<"" | "oelox" | "partner" | "needs_review">("");
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [issuedPasscode, setIssuedPasscode] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [editTarget, setEditTarget] = useState<AdminClient | null>(null);
  const [editForm, setEditForm] = useState({
    name: "",
    phone: "",
    address: "",
    username: "",
    email: "",
  });
  const [deleteTarget, setDeleteTarget] = useState<AdminClient | null>(null);
  const [createForm, setCreateForm] = useState({
    name: "",
    phone: "",
    address: "",
    username: "",
    email: "",
    partnerId: "",
  });

  const query = useQuery({
    queryKey: ["admin-clients", search, status, kind, page],
    queryFn: () =>
      fetchAdminClients({
        search,
        status: status || undefined,
        kind: kind || undefined,
        page,
        limit: 25,
      }),
  });

  const partnersQuery = useQuery({
    queryKey: ["admin-partners-select"],
    queryFn: () => fetchAdminPartners({ page: 1, limit: 200 }),
    enabled: createOpen,
  });

  const createMutation = useMutation({
    mutationFn: () =>
      createAdminBusinessOnboarding({
        name: createForm.name.trim(),
        phone: createForm.phone.trim(),
        address: createForm.address.trim(),
        username: createForm.username.trim().toLowerCase(),
        email: createForm.email.trim() || undefined,
        partnerId: createForm.partnerId.trim() || undefined,
      }),
    onSuccess: (data) => {
      setIssuedPasscode(data.passcode || null);
      setCreateOpen(false);
      const wasPartner = Boolean(createForm.partnerId.trim());
      setCreateForm({ name: "", phone: "", address: "", username: "", email: "", partnerId: "" });
      qc.invalidateQueries({ queryKey: ["admin-clients"] });
      toast.success(
        wasPartner ? "Partner Client passcode created" : "OELOX Client passcode created"
      );
    },
    onError: (err: Error) => toast.error(friendlyClientError(err)),
  });

  const statusMut = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      updateAdminClientStatus(id, status),
    onSuccess: async () => {
      toast.success("Client status updated");
      await qc.invalidateQueries({ queryKey: ["admin-clients"] });
    },
    onError: (err: Error) => toast.error(friendlyClientError(err)),
  });

  const editMut = useMutation({
    mutationFn: () =>
      updateAdminClientProfile(editTarget!.id, {
        name: editForm.name.trim(),
        phone: editForm.phone.trim(),
        address: editForm.address.trim(),
        username: editForm.username.trim().toLowerCase(),
        email: editForm.email.trim(),
      }),
    onSuccess: async () => {
      toast.success("Client updated");
      setEditTarget(null);
      await qc.invalidateQueries({ queryKey: ["admin-clients"] });
    },
    onError: (err: Error) => toast.error(friendlyClientError(err)),
  });

  const deleteMut = useMutation({
    mutationFn: () => deleteAdminClient(deleteTarget!.id),
    onSuccess: async () => {
      toast.success("Client deleted — status moved to cancelled");
      setDeleteTarget(null);
      await qc.invalidateQueries({ queryKey: ["admin-clients"] });
    },
    onError: (err: Error) => toast.error(friendlyClientError(err)),
  });

  const openEdit = async (row: AdminClient) => {
    try {
      const data = await qc.fetchQuery({
        queryKey: ["admin-client", row.id],
        queryFn: () => fetchAdminClient(row.id),
      });
      const c = (data.client ?? {}) as Record<string, unknown>;
      setEditForm({
        name: String(c.name ?? row.name ?? ""),
        phone: String(c.phone ?? ""),
        address: String(c.address ?? ""),
        username: String(c.username ?? row.username ?? ""),
        email: String(c.email ?? ""),
      });
    } catch {
      setEditForm({
        name: row.name ?? "",
        phone: "",
        address: "",
        username: row.username ?? "",
        email: "",
      });
    }
    setEditTarget(row);
  };

  const columns = useMemo(
    () => [
      {
        key: "name",
        header: "Client",
        render: (row: AdminClient) => (
          <div className="admin-row-main">
            <span className="admin-row-avatar" aria-hidden>
              <Building2 className="h-4 w-4" strokeWidth={2.25} />
            </span>
            <div className="min-w-0">
              <p className="admin-row-title">{row.name}</p>
              <p className="admin-row-sub">
                {row.username ? `@${row.username}` : row.slug}
              </p>
            </div>
          </div>
        ),
      },
      {
        key: "client_type",
        header: "Type",
        render: (row: AdminClient) => (
          <div className="flex flex-col gap-1">
            <span
              className={statusPillClass(row.client_type === "partner" ? "online" : "offline")}
            >
              {clientTypeLabel(row.client_type)}
            </span>
            {row.client_type_status && row.client_type_status !== "confirmed" ? (
              <span className="inline-flex items-center gap-1 text-xs text-amber-700">
                <AlertTriangle className="h-3 w-3" />
                {clientTypeStatusLabel(row.client_type_status)}
              </span>
            ) : null}
          </div>
        ),
      },
      {
        key: "partner_names",
        header: "Controlling partner",
        render: (row: AdminClient) =>
          row.controlling_partner_name || row.partner_names ? (
            <span className="inline-flex items-center gap-1 text-sm">
              <Handshake className="h-3.5 w-3.5" />
              {row.controlling_partner_name || row.partner_names}
            </span>
          ) : (
            <span className="text-[var(--text-muted)]">—</span>
          ),
      },
      {
        key: "status",
        header: "Status",
        render: (row: AdminClient) => (
          <span className={statusPillClass(row.status)}>{row.status}</span>
        ),
      },
      {
        key: "wallet_balance",
        header: "Wallet",
        render: (row: AdminClient) => (
          <div className="flex flex-col gap-0.5">
            <span className="admin-money">
              <Wallet className="h-3.5 w-3.5" />
              {formatIqd(row.wallet_balance ?? 0)}
            </span>
            {row.billing_payer === "partner" ? (
              <span className="text-[11px] text-[var(--text-muted)]">via partner wallet</span>
            ) : null}
          </div>
        ),
      },
      {
        key: "order_count",
        header: "Orders",
        render: (row: AdminClient) => (
          <span className="tabular font-bold">{formatNumber(row.order_count)}</span>
        ),
      },
      {
        key: "completed_trips",
        header: "Trips",
        render: (row: AdminClient) => (
          <span className="tabular font-bold">{formatNumber(row.completed_trips ?? 0)}</span>
        ),
      },
      {
        key: "created_at",
        header: "Created",
        render: (row: AdminClient) => (
          <span className="text-[var(--text-muted)]">{formatDate(row.created_at)}</span>
        ),
      },
      {
        key: "actions",
        header: "Actions",
        render: (row: AdminClient) => (
          <div className="flex flex-wrap gap-1.5" onClick={(e) => e.stopPropagation()}>
            <Button
              type="button"
              variant="secondary"
              className="btn-sm"
              icon={<Eye className="h-3.5 w-3.5" />}
              onClick={() => navigate(clientDetailPath(row.id))}
            >
              View
            </Button>
            <Button
              type="button"
              variant="secondary"
              className="btn-sm"
              icon={<Pencil className="h-3.5 w-3.5" />}
              onClick={() => void openEdit(row)}
            >
              Edit
            </Button>
            <Button
              type="button"
              variant="secondary"
              className="btn-sm"
              icon={<Power className="h-3.5 w-3.5" />}
              onClick={() =>
                statusMut.mutate({
                  id: row.id,
                  status: row.status === "active" ? "suspended" : "active",
                })
              }
            >
              {row.status === "active" ? "Suspend" : row.status === "cancelled" ? "Restore" : "Activate"}
            </Button>
            <Button
              type="button"
              variant="secondary"
              className="btn-sm"
              icon={<Trash2 className="h-3.5 w-3.5" />}
              onClick={() => setDeleteTarget(row)}
            >
              Delete
            </Button>
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const totalPages = Math.max(1, Math.ceil((query.data?.total ?? 0) / 25));
  const items = query.data?.items ?? [];

  async function copyPasscode() {
    if (!issuedPasscode) return;
    await navigator.clipboard.writeText(issuedPasscode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="menu-page">
      <PageHeader
        eyebrow="Platform"
        title="Clients"
        subtitle="Businesses that source orders — restaurants, pharmacies, groceries, cake shops, and other retailers. Billing runs per completed trip: direct clients bill the client wallet; partner clients bill the consolidated partner wallet."
        actions={
          <button
            type="button"
            className="admin-btn admin-btn--primary"
            onClick={() => setCreateOpen(true)}
          >
            <Plus className="h-4 w-4" />
            Add client
          </button>
        }
      />

      {issuedPasscode ? (
        <div className="admin-panel mb-4 p-4">
          <p className="text-sm font-bold mb-2">One-time setup passcode (expires in ~2 hours)</p>
          <div className="flex flex-wrap items-center gap-2">
            <code className="text-2xl font-black tracking-widest">{issuedPasscode}</code>
            <button type="button" className="admin-btn admin-btn--ghost" onClick={copyPasscode}>
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              Copy
            </button>
            <button
              type="button"
              className="admin-btn admin-btn--ghost"
              onClick={() => setIssuedPasscode(null)}
            >
              Dismiss
            </button>
          </div>
          <p className="text-xs text-[var(--text-muted)] mt-2">
            Client installs OX Store → First-time setup → enters this code.
          </p>
        </div>
      ) : null}

      <AdminPanel
        toolbar={
          <>
            <SearchBar
              value={search}
              onChange={(e) => {
                setPage(1);
                setSearch(e.target.value);
              }}
              placeholder="Search by name or slug…"
              className="admin-search"
            />
            <div className="customer-filter-chips admin-filter-chips">
              {KIND_FILTERS.map((f) => (
                <button
                  key={f.key || "all-kind"}
                  type="button"
                  className={`customer-filter-chip ${kind === f.key ? "customer-filter-chip--active" : ""}`}
                  onClick={() => {
                    setPage(1);
                    setKind(f.key);
                  }}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <div className="customer-filter-chips admin-filter-chips">
              {STATUS_FILTERS.map((f) => (
                <button
                  key={f.key || "all"}
                  type="button"
                  className={`customer-filter-chip ${status === f.key ? "customer-filter-chip--active" : ""}`}
                  onClick={() => {
                    setPage(1);
                    setStatus(f.key);
                  }}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </>
        }
      >
        {query.isLoading ? (
          <div className="space-y-2 p-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-14 rounded-xl skeleton" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={<Building2 className="h-5 w-5" />}
            title="No clients found"
            body="Try another search or onboard a new business client."
          />
        ) : (
          <div className="admin-table-wrap">
            <TableFlat
              columns={columns}
              data={items}
              keyField="id"
              onRowClick={(row) => navigate(clientDetailPath(row.id))}
            />
          </div>
        )}
      </AdminPanel>

      <AdminPager
        page={page}
        totalPages={totalPages}
        total={query.data?.total ?? 0}
        label="clients"
        onPrev={() => setPage((p) => p - 1)}
        onNext={() => setPage((p) => p + 1)}
      />

      <FormModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Onboard client business"
        subtitle="Default: OELOX Client. Selecting a controlling partner mints a Partner Client (stored client_type)."
        eyebrow="Clients"
        icon={<Plus className="w-5 h-5" />}
        footer={
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-ghost" onClick={() => setCreateOpen(false)}>
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary"
              disabled={
                createMutation.isPending ||
                !createForm.name.trim() ||
                !createForm.phone.trim() ||
                !createForm.address.trim() ||
                !createForm.username.trim()
              }
              onClick={() => createMutation.mutate()}
            >
              {createMutation.isPending ? "Creating…" : "Generate passcode"}
            </button>
          </div>
        }
      >
        <div className="space-y-3 p-1">
          <label className="block text-sm font-medium text-[var(--text-muted)]">
            Business name
            <input
              className="input-base mt-1"
              required
              value={createForm.name}
              onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="e.g. Al-Noor Pharmacy"
            />
          </label>
          <label className="block text-sm font-medium text-[var(--text-muted)]">
            Phone
            <input
              className="input-base mt-1"
              required
              value={createForm.phone}
              onChange={(e) => setCreateForm((f) => ({ ...f, phone: e.target.value }))}
            />
          </label>
          <label className="block text-sm font-medium text-[var(--text-muted)]">
            Address
            <input
              className="input-base mt-1"
              required
              value={createForm.address}
              onChange={(e) => setCreateForm((f) => ({ ...f, address: e.target.value }))}
            />
          </label>
          <label className="block text-sm font-medium text-[var(--text-muted)]">
            Username (client login ID)
            <input
              className="input-base mt-1"
              required
              value={createForm.username}
              onChange={(e) => setCreateForm((f) => ({ ...f, username: e.target.value }))}
              placeholder="e.g. alnoor-pharmacy"
              autoCapitalize="none"
              spellCheck={false}
            />
            <span className="text-xs text-[var(--text-muted)]">
              3–30 chars: a-z, 0-9, dash only. The client logs in with this — not email.
            </span>
          </label>
          <label className="block text-sm font-medium text-[var(--text-muted)]">
            Email (optional — used later for invoices)
            <input
              className="input-base mt-1"
              type="email"
              value={createForm.email}
              onChange={(e) => setCreateForm((f) => ({ ...f, email: e.target.value }))}
            />
          </label>
          <label className="block text-sm font-medium text-[var(--text-muted)]">
            Controlling partner (optional → Partner Client)
            <select
              className="input-base mt-1"
              value={createForm.partnerId}
              onChange={(e) => setCreateForm((f) => ({ ...f, partnerId: e.target.value }))}
            >
              <option value="">None — OELOX Client</option>
              {(partnersQuery.data?.items ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
        </div>
      </FormModal>

      <FormModal
        open={!!editTarget}
        onClose={() => setEditTarget(null)}
        title="Edit client"
        subtitle="Business name, contact, address and login username. Status and wallet are managed from the client page."
        eyebrow="Clients"
        icon={<Pencil className="w-5 h-5" />}
        panelClassName="max-w-2xl"
        footer={
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => setEditTarget(null)}>
              Cancel
            </Button>
            <Button
              type="button"
              disabled={
                editMut.isPending ||
                !editForm.name.trim() ||
                !editForm.phone.trim() ||
                !editForm.username.trim()
              }
              onClick={() => editMut.mutate()}
            >
              {editMut.isPending ? "Saving…" : "Save changes"}
            </Button>
          </div>
        }
      >
        <div className="space-y-3 p-1">
          <label className="block text-sm font-medium text-[var(--text-muted)]">
            Business name
            <input
              className="input-base mt-1"
              value={editForm.name}
              onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
            />
          </label>
          <label className="block text-sm font-medium text-[var(--text-muted)]">
            Phone
            <input
              className="input-base mt-1"
              value={editForm.phone}
              onChange={(e) => setEditForm((f) => ({ ...f, phone: e.target.value }))}
            />
          </label>
          <label className="block text-sm font-medium text-[var(--text-muted)]">
            Address
            <input
              className="input-base mt-1"
              value={editForm.address}
              onChange={(e) => setEditForm((f) => ({ ...f, address: e.target.value }))}
            />
          </label>
          <label className="block text-sm font-medium text-[var(--text-muted)]">
            Username (login ID)
            <input
              className="input-base mt-1"
              value={editForm.username}
              autoCapitalize="none"
              spellCheck={false}
              onChange={(e) => setEditForm((f) => ({ ...f, username: e.target.value }))}
            />
          </label>
          <label className="block text-sm font-medium text-[var(--text-muted)]">
            Email (optional)
            <input
              className="input-base mt-1"
              type="email"
              value={editForm.email}
              onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))}
            />
          </label>
        </div>
      </FormModal>

      <FormModal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Delete client?"
        subtitle="Soft delete — the client is hidden from active lists and their setup passcodes are revoked. Records are kept."
        eyebrow="Clients"
        icon={<Trash2 className="w-5 h-5" />}
        panelClassName="max-w-xl"
        footer={
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button
              type="button"
              loading={deleteMut.isPending}
              onClick={() => deleteMut.mutate()}
            >
              Delete client
            </Button>
          </div>
        }
      >
        <p className="p-1 text-sm text-[var(--text-secondary)]">
          {deleteTarget ? `“${deleteTarget.name}” will be moved to cancelled. You can restore it later by setting status back to active.` : ""}
        </p>
      </FormModal>
    </div>
  );
}
